/**
 * Citation Verification Tool - Comment Creator
 * 
 * Creates inline comments at citation locations with verification results
 */

import { createFileLogger } from '../../../utils/logger';
import { CommandExecutionContext } from '../../../types/commands';
import { VerificationResult } from './types';
import { docsAddCommentCommand } from '../../../apps/DocsApp/commands/v5/docs_add_comment';
import { createDocumentReader } from '../../../apps/DocsApp/services/doc/DocumentReader';

const log = createFileLogger('VERIFY_CITATIONS_COMMENTER', { enabled: false });

/**
 * Create inline comments for verification results
 * 
 * Uses docs_add_comment command to create comments at each citation location.
 * Comments include verdict, confidence, paper info, and analysis.
 */
export async function createVerificationComments(
  manuscriptId: string,
  verifications: VerificationResult[],
  context: CommandExecutionContext
): Promise<Map<string, string>> {
  log.info('[createVerificationComments] Creating inline comments:', {
    manuscriptId,
    citationCount: verifications.length
  });
  
  const commentThreadIds = new Map<string, string>(); // citationKey -> threadId
  
  // Read document structure to map character positions to node IDs
  const { reader, error: readerError } = await createDocumentReader(manuscriptId, context);
  if (readerError || !reader) {
    log.error('[createVerificationComments] Failed to read document:', readerError);
    return commentThreadIds;
  }
  
  const allContent = reader.readAll();
  const allNodes = allContent.nodes || [];
  
  if (allNodes.length === 0) {
    log.warn('[createVerificationComments] Document has no nodes');
    return commentThreadIds;
  }
  
  // Build character position to node mapping
  // CRITICAL: This must match the EXACT format used by doc_read_v2 markdown output
  // Format: <!-- node_id -->\ntext\n\n<!-- next_node_id -->\ntext
  // The \n\n is only between nodes (from .join('\n\n')), not after the last one
  let charPos = 0;
  const nodeMap: Array<{ nodeId: string; startPos: number; endPos: number; text: string; markdownStartPos: number }> = [];
  
  for (let i = 0; i < allNodes.length; i++) {
    const node = allNodes[i];
    const nodeText = (node.text || '').trim(); // doc_read trims each node text
    const nodeCommentTag = `<!-- ${node.id} -->\n`;
    const markdownStartPos = charPos + nodeCommentTag.length;
    const isLastNode = i === allNodes.length - 1;
    
    // Calculate end position for this node
    const nodeEndPos = charPos + nodeCommentTag.length + nodeText.length;
    
    nodeMap.push({
      nodeId: node.id,
      startPos: charPos,
      endPos: nodeEndPos,
      text: nodeText,
      markdownStartPos: markdownStartPos // Position where actual text starts in markdown
    });
    
    // Advance position: comment + text + separator (if not last node)
    charPos = nodeEndPos;
    if (!isLastNode) {
      charPos += 2; // +2 for "\n\n" between nodes
    }
  }
  
  log.info('[createVerificationComments] Built node map:', {
    totalNodes: nodeMap.length,
    totalChars: charPos,
    nodeDetails: nodeMap.map(n => ({
      nodeId: n.nodeId,
      startPos: n.startPos,
      endPos: n.endPos,
      markdownStartPos: n.markdownStartPos,
      textLength: n.text.length
    }))
  });
  
  // Build comments array - one comment per citation (no grouping)
  const commentsToAdd: any[] = [];
  
  for (const verification of verifications) {
    const location = verification.citation.location;
    
    // Skip if no location data or if citation wasn't found in text (charOffset === -1)
    if (!location || location.charOffset === undefined || location.charOffset < 0) {
      log.warn(`[createVerificationComments] Skipping citation without valid location: ${verification.citation.citationKey}`);
      continue;
    }
    
    const charOffset = location.charOffset;
    
    log.info(`[createVerificationComments] Processing citation:`, {
      citationKey: verification.citation.citationKey,
      charOffset,
      nodeMapBounds: `0-${nodeMap[nodeMap.length - 1].endPos}`
    });
    
    // Find which node contains this position
    // Position is in the markdown format (includes HTML comment tags)
    const foundNode = nodeMap.find(n => charOffset >= n.startPos && charOffset < n.endPos);
    
    if (!foundNode) {
      log.warn(`[createVerificationComments] Could not find node for position ${charOffset}`, {
        charOffset,
        totalChars: nodeMap[nodeMap.length - 1].endPos,
        nodeRanges: nodeMap.map(n => `${n.nodeId}: ${n.startPos}-${n.endPos}`)
      });
      continue;
    }
    
    // Find the actual citation text at this position in the node
    // Convert markdown position to node-relative position
    const relativePos = charOffset - foundNode.markdownStartPos;
    const nodeText = foundNode.text;
    
    log.info(`[createVerificationComments] Found node:`, {
      nodeId: foundNode.nodeId,
      charOffset,
      markdownStartPos: foundNode.markdownStartPos,
      relativePos,
      nodeTextLength: nodeText.length
    });
    
    // Target just this specific citation, not the whole multi-citation block
    // For multi-citations like "(Author1, 2020; Author2, 2021)", we want separate
    // comments on "Author1, 2020" and "Author2, 2021" individually
    
    // Extract the core citation without outer parentheses if present
    const citationKey = verification.citation.citationKey;
    const coreKey = citationKey.replace(/^\(|\)$/g, '').trim();
    
    // Check if this is part of a multi-citation by looking for the core key
    // If found standalone with parentheses, use full key; otherwise use core
    let targetText = citationKey;
    
    if (nodeText.indexOf(citationKey) === -1) {
      // Full citation key not found - likely part of multi-citation
      // Target just the core part (e.g., "Manthey et al., 2022" within "(Manthey et al., 2022; WHO, 2025)")
      if (nodeText.indexOf(coreKey) !== -1) {
        targetText = coreKey;
        log.info(`[createVerificationComments] Using core citation (part of multi-citation):`, { 
          original: citationKey,
          targetText: coreKey
        });
      } else {
        // Even core not found - this shouldn't happen but log it
        log.warn(`[createVerificationComments] Citation not found in node text:`, {
          citationKey,
          coreKey,
          nodeTextPreview: nodeText.substring(Math.max(0, relativePos - 50), Math.min(nodeText.length, relativePos + 100))
        });
      }
    }
    
    log.info(`[createVerificationComments] Citation targeting:`, {
      charOffset,
      nodeId: foundNode.nodeId,
      targetText,
      citationKey,
      isMultiCitation: targetText === coreKey
    });
    
    // Build comment body for this single citation
    const commentBody = buildCommentBody(verification);
    
    commentsToAdd.push({
      nodeId: foundNode.nodeId,
      targetText,
      commentBody,
      // Store metadata for later retrieval
      _citationKey: verification.citation.citationKey,
      _verification: verification
    });
  }
  
  log.info(`[createVerificationComments] Built comments:`, {
    totalComments: commentsToAdd.length,
    totalVerifications: verifications.length,
    skipped: verifications.length - commentsToAdd.length
  });
  
  if (commentsToAdd.length === 0) {
    log.warn('[createVerificationComments] No comments to add');
    return commentThreadIds;
  }
  
  // Process comments in batches of 20 (docs_add_comment limit)
  const BATCH_SIZE = 20;
  let totalAdded = 0;
  let totalFailed = 0;
  
  for (let i = 0; i < commentsToAdd.length; i += BATCH_SIZE) {
    const batch = commentsToAdd.slice(i, i + BATCH_SIZE);
    
    try {
      log.info(`[createVerificationComments] Adding batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(commentsToAdd.length / BATCH_SIZE)}:`, {
        batchSize: batch.length
      });
      
      const result = await docsAddCommentCommand.handler(
        { id: manuscriptId, comments: batch },
        context
      );
      
      totalAdded += result.data?.successCount || 0;
      totalFailed += result.data?.failCount || 0;
      
      // Extract thread IDs from results if available
      if (result.data?.results) {
        for (let j = 0; j < result.data.results.length; j++) {
          const resultItem = result.data.results[j];
          if (resultItem.success && resultItem.threadId) {
            const comment = batch[j];
            if (comment._citationKey) {
              commentThreadIds.set(comment._citationKey, resultItem.threadId);
            }
          }
        }
      }
      
      log.info(`[createVerificationComments] Batch complete:`, {
        added: result.data?.successCount || 0,
        failed: result.data?.failCount || 0
      });
      
    } catch (error: any) {
      log.error(`[createVerificationComments] Failed to add comment batch:`, error);
      totalFailed += batch.length;
    }
  }
  
  log.info('[createVerificationComments] Comments created:', {
    successCount: totalAdded,
    failedCount: totalFailed,
    totalCount: verifications.length
  });
  
  return commentThreadIds;
}

/**
 * Build comment body with verification details
 */
function buildCommentBody(verification: VerificationResult): string {
  const { citation, verdict, confidence, explanation, resolvedPaper, evidenceQuote, suggestedRevision } = verification;
  
  let body = '';
  
  // Citation being verified
  body += `**Citation:** ${citation.citationKey}\n\n`;
  
  // Header with verdict and confidence
  const verdictLabel = formatVerdict(verdict);
  const confidenceEmoji = confidence >= 75 ? '✅' : confidence >= 50 ? '⚠️' : '❌';
  
  body += `${confidenceEmoji} **${verdictLabel}** (${confidence}% confidence)\n\n`;
  
  // Paper info
  if (resolvedPaper) {
    const authors = resolvedPaper.authors.slice(0, 2).map(a => a.name).join(', ');
    const authorsText = resolvedPaper.authors.length > 2 ? `${authors} et al.` : authors;
    body += `**Paper:** ${resolvedPaper.title}\n`;
    body += `**Authors:** ${authorsText} (${resolvedPaper.year})\n\n`;
  }
  
  // Explanation
  body += `**Analysis:** ${explanation}\n\n`;
  
  // Evidence quote
  if (evidenceQuote) {
    body += `**Evidence:** "${evidenceQuote}"\n\n`;
  }
  
  // Suggested revision for issues
  if (suggestedRevision) {
    body += `**Suggested revision:** "${suggestedRevision}"\n\n`;
  }
  
  // Link to full report
  body += `_View full report for all citations_`;
  
  return body;
}

/**
 * Format verdict for display
 */
function formatVerdict(verdict: string): string {
  const labels: Record<string, string> = {
    strongly_supports: 'Strongly Supports',
    supports: 'Supports',
    weak_support: 'Weak Support',
    not_addressed: 'Not Addressed',
    contradicts: 'Contradicts',
    overstated_strength: 'Overstated Strength',
    overstated_generalizability: 'Overstated Generalizability',
    cherry_picked: 'Cherry-Picked',
    error: 'Error'
  };
  return labels[verdict] || verdict;
}

